const STATE_COUNT = 3;
const DIMENSION_COUNT = 2;
const TRAIN_FRACTION = 0.7;
const MIN_CANDLES = 400;
const MIN_TRAINING_OBSERVATIONS = 200;
const MIN_VALIDATION_OBSERVATIONS = 60;
const MAX_EM_ITERATIONS = 12;
const VARIANCE_FLOOR = 0.04;

const clampPeriod = (value) => Math.max(2, Math.min(500, Math.floor(Number(value) || 20)));

function logSumExp3(a, b, c) {
  const max = Math.max(a, b, c);
  if (!Number.isFinite(max)) return max;
  return max + Math.log(Math.exp(a - max) + Math.exp(b - max) + Math.exp(c - max));
}

function logGaussian(observation, means, variances, offset = 0) {
  let result = 0;
  for (let d = 0; d < DIMENSION_COUNT; d += 1) {
    const variance = Math.max(VARIANCE_FLOOR, variances[offset + d]);
    const delta = observation[d] - means[offset + d];
    result += -0.5 * (Math.log(2 * Math.PI * variance) + (delta * delta) / variance);
  }
  return result;
}

function makeFeatures(candles, period) {
  const valid = (candles || []).filter((bar) =>
    Number.isFinite(Number(bar?.time)) && Number.isFinite(Number(bar?.close)) && Number(bar.close) > 0
  );
  const returns = new Array(valid.length).fill(NaN);
  for (let i = 1; i < valid.length; i += 1) {
    returns[i] = Math.log(Number(valid[i].close) / Number(valid[i - 1].close));
  }

  const observations = [];
  for (let i = period; i < valid.length; i += 1) {
    let sum = 0;
    let absoluteSum = 0;
    let squaredSum = 0;
    let usable = true;
    for (let j = i - period + 1; j <= i; j += 1) {
      const value = returns[j];
      if (!Number.isFinite(value)) {
        usable = false;
        break;
      }
      sum += value;
      absoluteSum += Math.abs(value);
      squaredSum += value * value;
    }
    if (!usable) continue;
    const realizedVolatility = Math.sqrt(squaredSum / period);
    const efficiency = absoluteSum > 0 ? Math.min(1, Math.abs(sum) / absoluteSum) : 0;
    observations.push({
      time: Number(valid[i].time),
      raw: [Math.log(Math.max(realizedVolatility, 1e-12)), efficiency],
    });
  }
  return observations;
}

function standardize(train, all) {
  const means = [0, 0];
  const scales = [0, 0];
  for (let d = 0; d < DIMENSION_COUNT; d += 1) {
    means[d] = train.reduce((sum, row) => sum + row.raw[d], 0) / train.length;
    const variance = train.reduce((sum, row) => sum + (row.raw[d] - means[d]) ** 2, 0) / train.length;
    scales[d] = Math.sqrt(variance) || 1;
  }
  return {
    centers: means,
    scales,
    values: all.map((row) => row.raw.map((value, d) => (value - means[d]) / scales[d])),
  };
}

function quantile(values, probability) {
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.max(0, Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * probability)));
  return sorted[index] ?? 0;
}

function initializeModel(observations) {
  const vol = observations.map((row) => row[0]);
  const efficiency = observations.map((row) => row[1]);
  const means = new Float64Array(STATE_COUNT * DIMENSION_COUNT);
  // Deterministic starting points: quiet/inefficient, efficient, and unusually volatile.
  [[0.35, 0.25], [0.55, 0.8], [0.9, 0.55]].forEach(([volQ, efficiencyQ], state) => {
    means[state * DIMENSION_COUNT] = quantile(vol, volQ);
    means[state * DIMENSION_COUNT + 1] = quantile(efficiency, efficiencyQ);
  });
  const variances = new Float64Array(STATE_COUNT * DIMENSION_COUNT).fill(1);
  const transitions = new Float64Array([0.9, 0.05, 0.05, 0.05, 0.9, 0.05, 0.05, 0.05, 0.9]);
  const initial = new Float64Array([1 / 3, 1 / 3, 1 / 3]);
  return { means, variances, transitions, initial };
}

function expectation(observations, model) {
  const length = observations.length;
  const emissions = new Float64Array(length * STATE_COUNT);
  const forward = new Float64Array(length * STATE_COUNT);
  const backward = new Float64Array(length * STATE_COUNT);
  const logTransitions = Array.from(model.transitions, (value) => Math.log(Math.max(value, 1e-12)));

  for (let t = 0; t < length; t += 1) {
    for (let state = 0; state < STATE_COUNT; state += 1) {
      emissions[t * STATE_COUNT + state] = logGaussian(observations[t], model.means, model.variances, state * DIMENSION_COUNT);
    }
  }

  for (let state = 0; state < STATE_COUNT; state += 1) {
    forward[state] = Math.log(Math.max(model.initial[state], 1e-12)) + emissions[state];
  }
  for (let t = 1; t < length; t += 1) {
    const prev = (t - 1) * STATE_COUNT;
    const curr = t * STATE_COUNT;
    for (let to = 0; to < STATE_COUNT; to += 1) {
      const incoming = logSumExp3(
        forward[prev] + logTransitions[to],
        forward[prev + 1] + logTransitions[STATE_COUNT + to],
        forward[prev + 2] + logTransitions[2 * STATE_COUNT + to]
      );
      forward[curr + to] = incoming + emissions[curr + to];
    }
  }

  const last = (length - 1) * STATE_COUNT;
  const logLikelihood = logSumExp3(forward[last], forward[last + 1], forward[last + 2]);
  backward[last] = 0;
  backward[last + 1] = 0;
  backward[last + 2] = 0;
  for (let t = length - 2; t >= 0; t -= 1) {
    const curr = t * STATE_COUNT;
    const next = (t + 1) * STATE_COUNT;
    for (let from = 0; from < STATE_COUNT; from += 1) {
      backward[curr + from] = logSumExp3(
        logTransitions[from * STATE_COUNT] + emissions[next] + backward[next],
        logTransitions[from * STATE_COUNT + 1] + emissions[next + 1] + backward[next + 1],
        logTransitions[from * STATE_COUNT + 2] + emissions[next + 2] + backward[next + 2]
      );
    }
  }

  const gamma = new Float64Array(length * STATE_COUNT);
  for (let t = 0; t < length; t += 1) {
    const offset = t * STATE_COUNT;
    let total = 0;
    for (let state = 0; state < STATE_COUNT; state += 1) {
      const value = Math.exp(forward[offset + state] + backward[offset + state] - logLikelihood);
      gamma[offset + state] = value;
      total += value;
    }
    if (total > 0) {
      for (let state = 0; state < STATE_COUNT; state += 1) gamma[offset + state] /= total;
    }
  }
  return { emissions, forward, backward, gamma, logLikelihood, logTransitions };
}

function fitModel(observations) {
  const model = initializeModel(observations);
  let effectiveWeights = new Float64Array(STATE_COUNT);
  for (let iteration = 0; iteration < MAX_EM_ITERATIONS; iteration += 1) {
    const { emissions, forward, backward, gamma, logLikelihood, logTransitions } = expectation(observations, model);
    const length = observations.length;
    const nextMeans = new Float64Array(STATE_COUNT * DIMENSION_COUNT);
    const nextVariances = new Float64Array(STATE_COUNT * DIMENSION_COUNT);
    effectiveWeights = new Float64Array(STATE_COUNT);

    for (let t = 0; t < length; t += 1) {
      for (let state = 0; state < STATE_COUNT; state += 1) {
        const weight = gamma[t * STATE_COUNT + state];
        effectiveWeights[state] += weight;
        for (let d = 0; d < DIMENSION_COUNT; d += 1) {
          nextMeans[state * DIMENSION_COUNT + d] += weight * observations[t][d];
        }
      }
    }
    for (let state = 0; state < STATE_COUNT; state += 1) {
      const weight = Math.max(effectiveWeights[state], 1e-8);
      for (let d = 0; d < DIMENSION_COUNT; d += 1) nextMeans[state * DIMENSION_COUNT + d] /= weight;
    }

    for (let t = 0; t < length; t += 1) {
      for (let state = 0; state < STATE_COUNT; state += 1) {
        const weight = gamma[t * STATE_COUNT + state];
        for (let d = 0; d < DIMENSION_COUNT; d += 1) {
          const delta = observations[t][d] - nextMeans[state * DIMENSION_COUNT + d];
          nextVariances[state * DIMENSION_COUNT + d] += weight * delta * delta;
        }
      }
    }
    for (let state = 0; state < STATE_COUNT; state += 1) {
      const weight = Math.max(effectiveWeights[state], 1e-8);
      for (let d = 0; d < DIMENSION_COUNT; d += 1) {
        nextVariances[state * DIMENSION_COUNT + d] = Math.max(VARIANCE_FLOOR, nextVariances[state * DIMENSION_COUNT + d] / weight);
      }
    }

    const nextTransitions = new Float64Array(STATE_COUNT * STATE_COUNT);
    for (let from = 0; from < STATE_COUNT; from += 1) {
      let rowTotal = 0;
      for (let to = 0; to < STATE_COUNT; to += 1) {
        let expected = 0;
        for (let t = 0; t < length - 1; t += 1) {
          const at = t * STATE_COUNT;
          const next = (t + 1) * STATE_COUNT;
          const logXi = forward[at + from] + logTransitions[from * STATE_COUNT + to]
            + emissions[next + to] + backward[next + to] - logLikelihood;
          expected += Math.exp(logXi);
        }
        const index = from * STATE_COUNT + to;
        nextTransitions[index] = expected + 0.01;
        rowTotal += nextTransitions[index];
      }
      for (let to = 0; to < STATE_COUNT; to += 1) nextTransitions[from * STATE_COUNT + to] /= rowTotal;
    }

    model.means = nextMeans;
    model.variances = nextVariances;
    model.transitions = nextTransitions;
    for (let state = 0; state < STATE_COUNT; state += 1) model.initial[state] = gamma[state];
    if (!Number.isFinite(logLikelihood)) throw new Error("The HMM fit did not converge to finite likelihood values.");
  }
  const finalExpectation = expectation(observations, model);
  const lastOffset = (observations.length - 1) * STATE_COUNT;
  const lastLogNormalizer = logSumExp3(
    finalExpectation.forward[lastOffset],
    finalExpectation.forward[lastOffset + 1],
    finalExpectation.forward[lastOffset + 2]
  );
  const filteredState = new Float64Array(STATE_COUNT);
  for (let state = 0; state < STATE_COUNT; state += 1) {
    filteredState[state] = Math.exp(finalExpectation.forward[lastOffset + state] - lastLogNormalizer);
  }
  const trainingStateIndexes = Array.from({ length: observations.length }, (_, index) => {
    const offset = index * STATE_COUNT;
    let best = 0;
    if (finalExpectation.gamma[offset + 1] > finalExpectation.gamma[offset + best]) best = 1;
    if (finalExpectation.gamma[offset + 2] > finalExpectation.gamma[offset + best]) best = 2;
    return best;
  });
  return { ...model, filteredState, trainingStateIndexes, effectiveWeights };
}

function predictHoldout(observations, model) {
  let filtered = new Float64Array(model.filteredState);
  const logTransitions = Array.from(model.transitions, (value) => Math.log(Math.max(value, 1e-12)));
  let logScore = 0;
  const stateIndexes = [];
  observations.forEach((observation) => {
    const joint = new Float64Array(STATE_COUNT);
    for (let to = 0; to < STATE_COUNT; to += 1) {
      const predicted = logSumExp3(
        Math.log(Math.max(filtered[0], 1e-12)) + logTransitions[to],
        Math.log(Math.max(filtered[1], 1e-12)) + logTransitions[STATE_COUNT + to],
        Math.log(Math.max(filtered[2], 1e-12)) + logTransitions[2 * STATE_COUNT + to]
      );
      joint[to] = predicted + logGaussian(observation, model.means, model.variances, to * DIMENSION_COUNT);
    }
    const normalizer = logSumExp3(joint[0], joint[1], joint[2]);
    logScore += normalizer;
    filtered = new Float64Array(joint.map((value) => Math.exp(value - normalizer)));
    let best = 0;
    if (filtered[1] > filtered[best]) best = 1;
    if (filtered[2] > filtered[best]) best = 2;
    stateIndexes.push(best);
  });
  return { stateIndexes, averageLogScore: logScore / observations.length };
}

function mapStateLabels(model) {
  const states = [0, 1, 2];
  const highVolatility = states.reduce((best, state) =>
    model.means[state * DIMENSION_COUNT] > model.means[best * DIMENSION_COUNT] ? state : best, 0
  );
  const others = states.filter((state) => state !== highVolatility);
  const trend = model.means[others[0] * DIMENSION_COUNT + 1] >= model.means[others[1] * DIMENSION_COUNT + 1]
    ? others[0]
    : others[1];
  const range = others.find((state) => state !== trend);
  return { [range]: "range", [trend]: "trend", [highVolatility]: "highVol" };
}

function scoreSingleGaussian(observations) {
  const means = [0, 0];
  const variances = [1, 1];
  return observations.reduce((sum, observation) => sum + logGaussian(observation, means, variances), 0) / observations.length;
}

/**
 * Fit a three-state diagonal-Gaussian HMM on the first 70% of loaded candles and
 * forward-filter the last 30%. Features are rolling log volatility and Kaufman-style
 * directional efficiency. The holdout log score is compared with a one-state Gaussian;
 * this is a data-fit diagnostic, not evidence of trading performance or causal alpha.
 */
export function detectHMMRegimes(candles, { lookback = 20 } = {}) {
  const period = clampPeriod(lookback);
  if (!Array.isArray(candles) || candles.length < MIN_CANDLES) {
    return { status: "insufficient", reason: `Load at least ${MIN_CANDLES} candles to fit and evaluate the HMM.`, bars: [] };
  }

  const features = makeFeatures(candles, period);
  const split = Math.floor(features.length * TRAIN_FRACTION);
  const training = features.slice(0, split);
  const validation = features.slice(split);
  if (training.length < MIN_TRAINING_OBSERVATIONS || validation.length < MIN_VALIDATION_OBSERVATIONS) {
    return { status: "insufficient", reason: "There are not enough usable candle returns after the HMM lookback window.", bars: [] };
  }

  try {
    const normalized = standardize(training, features);
    const trainValues = normalized.values.slice(0, split);
    const validationValues = normalized.values.slice(split);
    const model = fitModel(trainValues);
    if ([...model.effectiveWeights].some((weight) => weight < 2)) {
      return { status: "unstable", reason: "The loaded candles did not support three distinct HMM states. Try a longer history or another timeframe.", bars: [] };
    }
    const prediction = predictHoldout(validationValues, model);
    const labels = mapStateLabels(model);
    const counts = { range: 0, trend: 0, highVol: 0 };
    const trainingBars = training.map((row, index) => {
      const kind = labels[model.trainingStateIndexes[index]];
      return { time: row.time, kind, phase: "fit" };
    });
    const validationBars = prediction.stateIndexes.map((state, index) => {
      const kind = labels[state];
      counts[kind] += 1;
      return { time: validation[index].time, kind, phase: "holdout" };
    });
    const baselineLogScore = scoreSingleGaussian(validationValues);
    return {
      status: "ready",
      reason: "",
      bars: [...trainingBars, ...validationBars],
      lookback: period,
      trainingBars: training.length,
      validationBars: validation.length,
      holdoutLogScore: prediction.averageLogScore,
      baselineLogScore,
      scoreDelta: prediction.averageLogScore - baselineLogScore,
      stateShare: Object.fromEntries(Object.entries(counts).map(([key, count]) => [key, count / validation.length])),
    };
  } catch (error) {
    return { status: "error", reason: error?.message || "The HMM could not be fitted to these candles.", bars: [] };
  }
}



// Backtest rule sources, attribution, and chart panels can share a fitted model for an
// immutable candle snapshot instead of refitting the same HMM for each selected state.
const cachedModels = new WeakMap();
export function detectHMMRegimesCached(candles, { lookback = 20 } = {}) {
  if (!Array.isArray(candles)) return detectHMMRegimes(candles, { lookback });
  const period = clampPeriod(lookback);
  const first = candles[0];
  const last = candles[candles.length - 1];
  const signature = `${candles.length}|${first?.time}|${first?.close}|${last?.time}|${last?.close}`;
  let entries = cachedModels.get(candles);
  if (!entries) {
    entries = new Map();
    cachedModels.set(candles, entries);
  }
  const cached = entries.get(period);
  if (cached?.signature === signature) return cached.result;
  const result = detectHMMRegimes(candles, { lookback: period });
  entries.set(period, { signature, result });
  return result;
}
